import type { JSX } from 'react'

import { useTranslation } from './i18n.js'
import { ConflictResolver } from './queue/ConflictResolver.js'
import { useWriteQueue } from './queue/use-write-queue.js'
import { refusalSentence } from './refusals.js'

/**
 * The blocked writes, and the two things a shop can do about them. §6.22 `OFF-005`.
 *
 * ---
 *
 * **Why this component has to exist.**
 *
 * The queue drains in order and a blocked write holds everything behind it, deliberately: a payment
 * that lands before the sale it pays for is not a race worth having. The consequence, which was not
 * thought through when that rule was written, is that **one refused write stops the shop**. Every
 * later prescription, sale and payment queues behind it, and before this component the only way out
 * was clearing the browser's site data.
 *
 * The status line was already honest about it, saying "1 need attention", and that made the gap
 * sharper rather than smaller: the screen told a shopkeeper something was wrong and gave them
 * nothing whatever to do about it. `OFF-005` is "Staff must never be uncertain whether a sale is
 * safe", and an unfixable warning is its own kind of uncertainty.
 *
 * Two actions, and both are a person's:
 *
 * - **Try again** for the case where the reason has gone. A 404 because the API had not been
 *   deployed, a 401 because the session had expired and has since been renewed. Retrying resets the
 *   backoff, because that counted a server being unreachable and a person is now saying it is not.
 * - **Discard** for the case where it cannot be sent and the shop has dealt with it another way.
 *   It loses whatever the write carried, so it asks first.
 *
 * Neither is automatic. A refusal that retried itself turns a validation failure into an infinite
 * loop, and one that discarded itself is a sale the shop believes happened.
 *
 * **A third case, where neither of those two actions works.** On a `VERSION_CONFLICT` the queued
 * body carries the `expectedVersion` that already lost the race, so Try again re-sends a stale
 * number and gets the same 409 for ever, and Discard throws away what this person typed. The only
 * working option a shopkeeper had was to lose their own work. `DESIGN-TOKENS.md` §16.5 asks for a
 * resolver showing "both versions side by side with an explicit choice", and the server has been
 * attaching the current state to every conflict all along: 81 call sites answer through
 * `conflictDetails`. `transport.ts` was dropping it.
 */
export function BlockedWrites(): JSX.Element | null {
  const { snapshot, retry, discard, resolve } = useWriteQueue()
  const { t } = useTranslation()
  const blocked = snapshot.writes.filter((w) => w.status === 'blocked')

  if (blocked.length === 0) return null

  return (
    <section className="lm-blocked" aria-labelledby="lm-blocked-heading">
      <h2 id="lm-blocked-heading" className="lm-blocked__heading">
        {t('blocked.heading', { count: blocked.length })}
      </h2>

      <ul className="lm-blocked__list">
        {blocked.map((write) => {
          /**
           * A conflict is resolvable only if the server attached what the record now holds. It
           * always does for a `VERSION_CONFLICT`, through `conflictDetails`, but a 409 from an
           * older API or a proxy that rewrote the body would not, and a resolver with nothing on
           * the other side is a comparison against a blank column. That case falls back to the two
           * ordinary actions.
           */
          const current = (write.error?.details as { current?: unknown } | undefined)?.current
          const currentVersion = (current as { version?: unknown } | undefined)?.version
          const resolvable =
            write.error?.code === 'VERSION_CONFLICT' && typeof currentVersion === 'number'

          return (
          <li key={write.id} className="lm-blocked__item">
            <p className="lm-blocked__label">{write.label}</p>
            {/* The Appendix F code and its message travel from the server unchanged, so what the
                shop reads is what the error catalogue wrote rather than something invented here. */}
            <p className="lm-blocked__reason">
              {refusalSentence(t, write.error?.code, write.error?.message) ??
                t('blocked.noReason')}
              {write.error?.code ? ` (${write.error.code})` : ''}
            </p>
            {resolvable ? (
              <ConflictResolver
                mine={write.body}
                theirs={current}
                onKeepMine={() => {
                  void resolve(write.id, currentVersion as number)
                }}
                onKeepTheirs={() => {
                  // The same loss Discard carries, so it asks the same way. Keeping theirs is not
                  // a softer choice than discarding: it is the same discard, named for what the
                  // person is choosing rather than for what the queue does.
                  const ok = window.confirm(t('blocked.confirmDiscard', { label: write.label }))
                  if (ok) void discard(write.id)
                }}
              />
            ) : null}

            <div className="lm-blocked__actions">
              {/* Try again is withheld on a resolvable conflict. It cannot work there: the body
                  carries the version that already lost, so the button's only effect is to spend a
                  round trip and come back to this same screen. */}
              {resolvable ? null : (
              <button
                type="button"
                className="lm-button lm-button--secondary"
                onClick={() => {
                  void retry(write.id)
                }}
              >
                {t('blocked.retry')}
              </button>
              )}
              <button
                type="button"
                className="lm-button lm-button--danger"
                onClick={() => {
                  // Discarding loses the prescription or the sale this write carried, and nothing
                  // else in the product will remember it. So the shop confirms, in words that say
                  // what is actually lost rather than "are you sure".
                  const ok = window.confirm(t('blocked.confirmDiscard', { label: write.label }))
                  if (ok) void discard(write.id)
                }}
              >
                {t('blocked.discard')}
              </button>
            </div>
          </li>
          )
        })}
      </ul>
    </section>
  )
}
